import {
  Home as HomeIcon, Briefcase, Trello, ClipboardList, LayoutGrid, StickyNote,
  Users, ClipboardCheck, Plane, MapPin, Building2, FolderKanban, FileSignature,
  CircleDollarSign, Receipt, FileSpreadsheet, Settings, ShieldAlert, Archive as ArchiveIcon,
  Hammer, UsersRound, Contact, Wallet, ShieldCheck, Clock, ReceiptText,
} from 'lucide-react';

// Single source of truth for navigation (Sep 2026): sidebar, mobile bottom
// bar, Ctrl-K search and landing-page choice all read from here.
export const ALL = ['admin', 'supervisor', 'user'];
export const MGR = ['admin', 'supervisor'];

export const NAV = [
  { key: 'home', name: 'Home', tKey: 'nav.home', path: '/home', icon: HomeIcon, roles: ALL, keywords: 'dashboard my day clock overview' },
  {
    key: 'work', name: 'Work', tKey: 'nav.section.work', icon: Hammer, roles: ALL,
    items: [
      { key: 'jobs', name: 'Jobs', tKey: 'nav.jobs', path: '/jobs', icon: Briefcase, roles: ALL, keywords: 'work tasks job list create job' },
      { key: 'jobs-board', name: 'Jobs Board', tKey: 'nav.jobsBoard', path: '/jobs/board', icon: Trello, roles: ALL, keywords: 'kanban board pipeline' },
      { key: 'workplans', name: 'Workplans', tKey: 'nav.workplans', path: '/workplans', icon: ClipboardList, roles: ALL, keywords: 'plan schedule' },
      { key: 'task-boards', name: 'Task Boards', tKey: 'nav.taskBoards', path: '/boards', icon: LayoutGrid, roles: ALL, keywords: 'boards cards tasks' },
      { key: 'notes', name: 'Notes', tKey: 'nav.notes', path: '/notes', icon: StickyNote, roles: ALL, keywords: 'memo note' },
    ],
  },
  {
    key: 'people', name: 'People', tKey: 'nav.section.people', icon: UsersRound, roles: ALL,
    items: [
      { key: 'staff', name: 'Staff', tKey: 'nav.staff', path: '/staff', icon: Users, roles: MGR, keywords: 'users employees workers add user invite team management my team' },
      { key: 'approvals', name: 'Approvals', tKey: 'nav.approvals', path: '/approvals', icon: ClipboardCheck, roles: MGR, badge: 'approvals', keywords: 'pending approvals timesheets amendments' },
      { key: 'time-off', name: 'Time Off', tKey: 'nav.timeOff', path: '/time-off', icon: Plane, roles: ALL, keywords: 'pto vacation leave' },
      { key: 'map', name: 'Live Map', tKey: 'nav.liveMap', path: '/map', icon: MapPin, roles: MGR, keywords: 'live gps locations map' },
    ],
  },
  {
    key: 'customers', name: 'Customers', tKey: 'nav.section.customers', icon: Contact, roles: ALL,
    items: [
      { key: 'customers', name: 'Customers', tKey: 'nav.customers', path: '/customers', icon: Building2, roles: MGR, keywords: 'clients customer add customer' },
      { key: 'projects', name: 'Projects', tKey: 'nav.projects', path: '/projects', icon: FolderKanban, roles: MGR, keywords: 'project quickbooks class' },
      { key: 'estimates', name: 'Estimates', tKey: 'nav.estimates', path: '/estimates', icon: FileSignature, roles: ALL, keywords: 'quotes proposals' },
    ],
  },
  {
    key: 'money', name: 'Money', tKey: 'nav.section.money', icon: Wallet, roles: ALL,
    items: [
      { key: 'payroll', name: 'Payroll', tKey: 'nav.payroll', path: '/payroll', icon: CircleDollarSign, roles: ['admin'], keywords: 'payroll review process push quickbooks preview compare csv lock manual' },
      { key: 'pay-runs', name: 'Pay Runs', tKey: 'nav.payRuns', path: '/pay-runs', icon: CircleDollarSign, roles: ['admin'], keywords: 'pay run gross pay statements vacation stat holiday register export' },
      { key: 'reimbursements', name: 'Reimbursements', tKey: 'nav.reimbursements', path: '/reimbursements', icon: ReceiptText, roles: ALL, keywords: 'reimbursement expense receipt gas mileage claim' },
      { key: 'invoices', name: 'Invoices', tKey: 'nav.invoices', path: '/invoices', icon: Receipt, roles: ['admin'], keywords: 'daily invoices billing subscription plan' },
    ],
  },
  { key: 'reports', name: 'Reports', tKey: 'nav.reports', path: '/reports', icon: FileSpreadsheet, roles: ALL, keywords: 'analytics payroll hours export' },
  {
    key: 'admin', name: 'Admin', tKey: 'nav.section.admin', icon: ShieldCheck, roles: MGR,
    items: [
      { key: 'settings', name: 'Settings', tKey: 'nav.settings', path: '/settings', icon: Settings, roles: MGR, keywords: 'preferences profile options company' },
      { key: 'audit-log', name: 'Audit Log', tKey: 'nav.auditLog', path: '/audit-log', icon: ShieldAlert, roles: ['admin'], keywords: 'audit history security log' },
      { key: 'archive', name: 'Archive', tKey: 'nav.archive', path: '/archive', icon: ArchiveIcon, roles: MGR, keywords: 'archived deleted old jobs' },
    ],
  },
];

export const WORKER_SETTINGS = { key: 'settings', name: 'Settings', tKey: 'nav.settings', path: '/settings', icon: Settings, roles: ['user'], keywords: 'preferences profile options' };

// Tabs inside merged pages — searchable as "Page › Tab".
export const MERGED_TABS = [
  { key: 'home-myday', name: 'Home › My Day', path: '/home?tab=myday', icon: Clock, roles: ALL, keywords: 'clock in my day today' },
  { key: 'home-company', name: 'Home › Company', path: '/home?tab=company', icon: HomeIcon, roles: MGR, keywords: 'dashboard company overview stats' },
  { key: 'staff-all', name: 'Staff › All Staff', path: '/staff?tab=all', icon: Users, roles: MGR, keywords: 'manage users employees' },
  { key: 'staff-my-team', name: 'Staff › My Team', path: '/staff?tab=my-team', icon: Users, roles: MGR, keywords: 'my team clock' },
  { key: 'staff-teams', name: 'Staff › Manage Teams', path: '/staff?tab=teams', icon: UsersRound, roles: MGR, keywords: 'teams kiosk squads supervisors create team' },
  { key: 'payroll-review', name: 'Payroll › Payroll Review', path: '/payroll?tab=review', icon: CircleDollarSign, roles: ['admin'], keywords: 'payroll review workbench' },
  { key: 'payroll-process', name: 'Payroll › Process & Push to QuickBooks', path: '/payroll?tab=process', icon: CircleDollarSign, roles: ['admin'], keywords: 'push quickbooks preview compare csv backup confirm lock' },
  { key: 'payroll-manual', name: 'Payroll › Payroll Manual', path: '/payroll?tab=manual', icon: CircleDollarSign, roles: ['admin'], keywords: 'payroll manual guide' },
  { key: 'invoices-daily', name: 'Invoices › Daily Invoices', path: '/invoices?tab=daily', icon: Receipt, roles: ['admin'], keywords: 'daily site invoices' },
  { key: 'invoices-billing', name: 'Invoices › Billing', path: '/invoices?tab=billing', icon: Receipt, roles: ['admin'], keywords: 'billing subscription plan seats' },
];

export const LANDING_OPTIONS = [
  { value: 'clock-in', label: 'Clock-in screen', path: '/clock-in', roles: ALL },
  { value: 'myday', label: 'Home › My Day', path: '/home?tab=myday', roles: ALL },
  { value: 'company', label: 'Home › Company dashboard', path: '/home?tab=company', roles: MGR },
  { value: 'jobs', label: 'Jobs', path: '/jobs', roles: ALL },
];
export const landingPath = (value) => (LANDING_OPTIONS.find((o) => o.value === value) || LANDING_OPTIONS[0]).path;

export const effectiveRole = (user) => (user?.role === 'admin' || user?.is_super_user ? 'admin' : user?.role || 'user');

// Role-filtered nav tree (groups with no visible children are dropped).
export const navForUser = (user) => {
  const role = effectiveRole(user);
  const allow = (n) => n.roles.includes(role);
  const out = NAV.filter(allow).map((n) => (n.items ? { ...n, items: n.items.filter(allow) } : n)).filter((n) => !n.items || n.items.length > 0);
  if (role === 'user') out.push(WORKER_SETTINGS);
  return out;
};
export const flatNavForUser = (user) => navForUser(user).flatMap((n) => (n.items ? n.items : [n]));
export const searchTargetsForUser = (user) => {
  const role = effectiveRole(user);
  return [...flatNavForUser(user), ...MERGED_TABS.filter((t) => t.roles.includes(role))];
};
